import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export const Projects = () => {
  const { language, t } = useLanguage();
  const seo = siteConfig.seo.projects;

  useEffect(() => {
    document.title = t(seo.title);
  }, [language, seo, t]);

  return (
    <>
      <div className="flex flex-col">
        {/* Hero */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="projects-title">
              {language === 'fr' ? 'Nos Réalisations' : 'Our Projects'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'fr'
                ? 'Découvrez nos projets d’automatisation industrielle à travers le Canada.'
                : 'Discover our industrial automation projects across Canada.'}
            </p>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-20" data-testid="projects-grid-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {siteConfig.projects.map((project, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  data-testid={`project-card-${idx}`}
                  className="flex"
                >
                  <Card className="overflow-hidden group hover:border-primary/50 transition-colors duration-300 w-full flex flex-col">
                    <div className="h-64 relative overflow-hidden flex-shrink-0">
                      <img
                        src={project.image}
                        alt={t(project.title)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">
                        {t(project.industry)}
                      </div>
                      <h3 className="font-heading text-lg font-semibold mb-3 line-clamp-2">
                        {t(project.title)}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 flex-1">{t(project.outcome)}</p>
                      <p className="text-xs text-muted-foreground mb-4">{project.technologies}</p>
                      {project.location && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
                          <MapPin className="h-3 w-3" />
                          {project.location}
                        </p>
                      )}
                      <Button
                        asChild
                        variant="outline"
                        className="w-full mt-auto"
                        data-testid={`project-cta-${idx}`}
                      >
                        <Link to="/contact">
                          {language === 'fr' ? "Discuter d'un projet similaire" : 'Discuss a Similar Project'}
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="projects-cta-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              {language === 'fr' ? 'Votre projet est le prochain' : 'Your Project Is Next'}
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Contactez-nous pour discuter de vos besoins en automatisation.'
                : 'Contact us to discuss your automation needs.'}
            </p>
            <Button asChild size="lg" variant="secondary" className="font-bold uppercase tracking-wide">
              <Link to="/contact">{t(siteConfig.brand.primaryCTA)}</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};